import React, { useContext, useEffect, useState } from "react";

import {
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import { UserLastSeen } from "@safetyculture/s12-apis-connect-web/s12/accounts/history/v1/history_pb";
import { FolderWithMembersCount } from "@safetyculture/s12-apis-connect-web/s12/directory/v1/folder_pb";
import { GroupData } from "@safetyculture/s12-apis-connect-web/s12/groups/v1/common_pb";
import {
  ContractStatus,
  GetContractsByOrgResponse,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/contracts_pb";
import {
  GetUserInspectionQuotaResponse,
  GetUserOrganisationsResponse,
  Role,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import UserOrgsRecord from "./components/user-orgs-record";
import { getOrgContractList } from "api/endpoints/contracts";
import {
  GetFoldersByUsers,
  listUsersLastSeen,
} from "api/endpoints/organisation";
import {
  getUserGroups,
  getUserInspectionQuota,
  getUserOrgs as getUserOrgsApi,
} from "api/endpoints/user";
import { callOptionsHeaders } from "api/helpers";
import { DocPrefix } from "constants/index";
import { SnackbarContext } from "context/snackbar-context";
import { toS12ID, toUUID } from "helpers/s12-helpers";
import { UserUrlParamTypes } from "types/user-display";

interface UserOrgGroupList {
  orgId: string;
  groupList: GroupData[];
}

interface UserOrgLastSeen {
  orgId: string;
  lastSeen: UserLastSeen[];
}

const UserOrgsTab = () => {
  const { regionCode, userId } = useParams<UserUrlParamTypes>();
  const [orgList, setOrgList] = useState<GetUserOrganisationsResponse>(
    new GetUserOrganisationsResponse({
      organisations: [],
      totalItems: 0,
    })
  );
  const [quotaList, setQuotaList] = useState<GetUserInspectionQuotaResponse[]>(
    []
  );
  const [userOrgsContractsList, setUserOrgsContractsList] = useState<
    GetContractsByOrgResponse[]
  >([]);
  const [userOrgGroups, setUserOrgGroups] = useState<UserOrgGroupList[]>([]);
  const [userLastSeenList, setUserLastSeenList] = useState<UserOrgLastSeen[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingQuotas, setIsLoadingQuotas] = useState(false);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [isLoadingLastSeen, setIsLoadingLastSeen] = useState(false);
  const [isLoadingContracts, setIsLoadingContracts] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [folders, setFolders] = useState<FolderWithMembersCount[]>([]);

  const { showError } = useContext(SnackbarContext);

  const getFoldersByUsers = () => {
    setFolders([]);
    const folders = Promise.all(
      orgList.organisations.map((org: Role) =>
        GetFoldersByUsers(
          {
            orgId: org.id,
            userIds: [userId],
          },
          callOptionsHeaders(regionCode, org.id)
        )
      )
    );

    folders
      .then((res) => {
        if (!res || res.length === 0) {
          return;
        }
        setFolders(res[0].userFolders[toUUID(userId)].folders);
      })
      .catch((err) => {
        showError(err);
      });
  };

  const getUserOrgs = () => {
    setIsLoading(true);
    getUserOrgsApi(
      {
        userId: toUUID(userId),
      },
      callOptionsHeaders(regionCode, userId)
    )
      .then((res) => {
        setOrgList(res);
      })
      .catch((err) => {
        setHasError(true);
        showError(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getContractsForUserOrgs = () => {
    setIsLoadingContracts(true);
    const contracts = Promise.all(
      orgList.organisations.map((org: Role) =>
        getOrgContractList(
          {
            orgId: toUUID(org.id),
          },
          callOptionsHeaders(regionCode, org.id)
        )
      )
    );
    contracts
      .then((res) => {
        setUserOrgsContractsList(res);
      })
      .catch(showError)
      .finally(() => {
        setIsLoadingContracts(false);
      });
  };

  const getUserQuotas = () => {
    setIsLoadingQuotas(true);
    const quotas = Promise.all(
      orgList.organisations.map((org: Role) =>
        getUserInspectionQuota(
          {
            userId: toUUID(userId),
            orgId: toUUID(org.id),
          },
          callOptionsHeaders(regionCode, userId)
        )
      )
    );
    quotas
      .then((res) => {
        setQuotaList(res);
      })
      .catch(showError)
      .finally(() => {
        setIsLoadingQuotas(false);
      });
  };

  const listUserGroups = () => {
    setIsLoadingGroups(true);

    if (orgList.organisations.length === 0) {
      setIsLoadingGroups(false);
    }

    Promise.all(
      orgList.organisations.map((org: Role) => {
        getUserGroups(
          {
            organizationId: toUUID(org.id),
            userId: toUUID(userId),
          },
          callOptionsHeaders(regionCode, userId)
        )
          .then((res) => {
            const groups = userOrgGroups;
            setUserOrgGroups([
              { orgId: org.id, groupList: res.groups },
              ...groups,
            ]);
          })
          .catch(showError)
          .finally(() => setIsLoadingGroups(false));
      })
    );
  };

  const getUserLastSeen = () => {
    setIsLoadingLastSeen(true);

    if (orgList.organisations.length === 0) {
      setIsLoadingLastSeen(false);
    }

    Promise.all(
      orgList.organisations.map((org: Role) => {
        listUsersLastSeen(
          {
            orgId: toUUID(org.id),
            userIds: [toUUID(userId)],
          },
          callOptionsHeaders(regionCode, userId)
        )
          .then((res) => {
            const userLastSeen = res.users.map((user) => {
              user.userId = toS12ID(DocPrefix.PrefixUser, user.userId);
              return user;
            });

            const lastSeenList = userLastSeenList;
            setUserLastSeenList([
              { orgId: org.id, lastSeen: userLastSeen },
              ...lastSeenList,
            ]);
          })
          .catch(showError)
          .finally(() => setIsLoadingLastSeen(false));
      })
    );
  };

  useEffect(() => {
    getUserOrgs();
    // Adding getUserOrgs in dep array would cause infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getUserQuotas();
    listUserGroups();
    getUserLastSeen();
    getContractsForUserOrgs();
    getFoldersByUsers();
    // Adding getUserOrgs in dep array would cause infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgList]);

  const populateOrgRecords = () => {
    if (hasError) {
      return (
        <TableCell colSpan={7} data-testid="error-loading-orgs" align="left">
          No user organisations were able to be loaded. Check for errors or data
          issues.
        </TableCell>
      );
    }

    return orgList.organisations.map((row: Role, index: number) => {
      const userGroupsWithinOrg = userOrgGroups.find((groups) => {
        return row.id === groups.orgId;
      });

      const userLastSeenWithinOrg = userLastSeenList.find((lastSeen) => {
        return row.id === lastSeen.orgId;
      });

      const orgContracts = userOrgsContractsList.find((contracts) => {
        return contracts?.contracts?.[0]?.organisationId === row.id;
      });

      const orgIsOnEnterprisePlan =
        orgContracts && orgContracts.contracts
          ? orgContracts.contracts.some(
              (e) =>
                e?.status === ContractStatus.ACTIVATED &&
                e?.hasEnterprise === true
            )
          : false;

      return (
        <UserOrgsRecord
          row={row}
          key={index}
          quota={quotaList[index]?.quota}
          userGroups={userGroupsWithinOrg?.groupList}
          userLastSeen={userLastSeenWithinOrg?.lastSeen}
          orgIsOnEnterprisePlan={!!orgIsOnEnterprisePlan}
          folders={folders}
        />
      );
    });
  };

  const classes = mainStyles();
  return (
    <React.Fragment>
      <Grid container sx={classes.paddingTopSmall}>
        <TableContainer component={Paper}>
          <Table aria-label="user-list-table">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: "20%", paddingLeft: "30px" }}>
                  <b>Organisation</b>
                </TableCell>
                <TableCell style={{ width: "20%" }}>
                  <b>User Groups</b>
                </TableCell>
                <TableCell style={{ width: "20%" }}>
                  <b>Site Memberships</b>
                </TableCell>
                <TableCell style={{ width: "20%" }}>
                  <b>Permissions</b>
                </TableCell>
                <TableCell style={{ width: "20%" }}>
                  <b>Billing</b>
                </TableCell>
                <TableCell style={{ width: "20%" }}>
                  <b>Seat Details</b>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ||
              isLoadingQuotas ||
              isLoadingGroups ||
              isLoadingContracts ||
              isLoadingLastSeen ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : (
                populateOrgRecords()
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </React.Fragment>
  );
};
export default UserOrgsTab;
